<template>
  <div>
    <div class="d-none d-lg-block nav-wrapper mb-3">
      <nav
        class="navbar navbar-expand-lg text-white bg-cr-red-black"
        :class="{'fixed-top': navbarFixed, 'nav-wide': sideNavCollapsed}"
      >
        <form
          @submit.prevent="$emit('filter')"
          class="w-100 d-flex align-items-center justify-content-between"
        >
          <div class="w-100">
            <div class="d-flex align-items-center">
              <div class="d-flex">
                <label class="custom-control custom-checkbox mt-2 mb-2 mr-2">
                  <input
                    type="checkbox"
                    id="filter-gender-f"
                    class="custom-control-input"
                    v-model="filters.gender.f"
                  />
                  <span class="custom-control-label">
                  {{ $t('word.female') }}
                </span>
                </label>
                <label class="custom-control custom-checkbox mt-2 mb-2 mr-2">
                  <input
                    type="checkbox"
                    id="filter-gender-m"
                    class="custom-control-input"
                    v-model="filters.gender.m"
                  >
                  <span class="custom-control-label">
                  {{ $t('word.male') }}
                </span>
                </label>
                <label class="custom-control custom-checkbox mt-2 mb-2 mr-2">
                  <input
                    type="checkbox"
                    id="filter-gender-o"
                    class="custom-control-input"
                    v-model="filters.gender.o"
                  >
                  <span class="custom-control-label">
                  {{ $t('word.other') }}
                </span>
                </label>
              </div>

              <span class="ml-4 mr-4 text-bold">|</span>

              <div class="d-flex align-items-center flex-nowrap mr-2">
                <input
                  type="number"
                  id="filter-age-min"
                  class="form-control form-control-inverted text-center input-age"
                  :placeholder="$t('phrase.age_from')"
                  v-model="filters.age.min"
                />
                <span class="text-bold ml-2 mr-2">-</span>
                <input
                  type="number"
                  id="filter-age-max"
                  class="form-control form-control-inverted text-center input-age"
                  :placeholder="$t('phrase.age_til')"
                  v-model="filters.age.max"
                />
              </div>

              <span class="ml-2 mr-3 text-bold">|</span>

              <div class="d-flex align-items-center flex-nowrap">
                <v-multi-select
                  :title="$t('word.region')"
                  id="filter-region"
                  :options="regionOptions"
                  v-model="filters.provinces"
                />
              </div>

              <span
                class="ml-3 mr-3 text-bold"
                v-if="filterableResearchItems.length > 0"
              >|</span>
              <button
                type="button"
                class="btn btn-link btn-sm text-white"
                @click="showAllFilters = !showAllFilters"
                v-if="filterableResearchItems.length > 0"
                id="filter-all-filters"
              >
                <i class="fa fa-list mr-2"></i>
                <span class="mr-2">
                  {{ $t('phrase.all_filters') }}
                </span>
                <span
                  class="badge badge-pill bg-dark text-white"
                  v-if="filters.researchItemFilters"
                >
                  {{ Object.keys(filters.researchItemFilters).length }}
                </span>
              </button>
              <span class="ml-3 mr-3 text-bold">|</span>
              <div class="d-flex flex-nowrap align-items-center">
                <button
                  type="button"
                  id="filter-reset"
                  class="btn btn-link text-white btn-sm"
                  @click="reset"
                  v-title="$t('word.reset')"
                >
                  <i class="fa fa-undo mr-2"></i>
                </button>
                <v-button
                  type="submit"
                  id="filter-filter"
                  style-type="dark"
                  size="sm"
                  :loader="filterLoader"
                  text="word.filter"
                  icon="filter"
                />
              </div>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <div>
              <v-dropdown-menu
                icon="ellipsis-v"
                style-type="secondary"
                size="sm"
                text="word.more"
                :loader="loader"
              >
                <template v-slot:menu>
                  <v-dropdown-menu-button
                    text="phrase.export_csv"
                    icon="download"
                    @click="exportFile('csv')"
                  />
                  <v-dropdown-menu-button
                    text="phrase.export_xlsx"
                    icon="download"
                    @click="exportFile('xlsx')"
                  />
                </template>
              </v-dropdown-menu>
            </div>
          </div>
        </form>
      </nav>
    </div>
    <div class="d-block d-lg-none mb-3">
      <nav class="navbar text-white bg-cr-red-black d-flex justify-content-between">
        <v-dropdown-menu
          icon="ellipsis-v"
          style-type="primary"
          text="word.more"
          :loader="loader"
        >
          <template v-slot:menu>
            <v-dropdown-menu-button
              text="phrase.export_csv"
              icon="download"
              @click="exportFile('csv')"
            />
            <v-dropdown-menu-button
              text="phrase.export_xlsx"
              icon="download"
              @click="exportFile('xlsx')"
            />
          </template>
        </v-dropdown-menu>
      </nav>
    </div>

    <v-research-filter-modal
      v-if="showAllFilters"
      :filters="filters"
      :filterable-research-items="filterableResearchItems"
      :country-code="research.country"
      @hide-modal="showAllFilters = false"
      @submit="submitFilters"
    />
  </div>
</template>

<script>
import Button from '@/common/components/Button.vue';
import DropdownMenu from '@/common/components/DropdownMenu.vue';
import DropdownMenuButton from '@/common/components/DropdownMenuButton.vue';
import MultiSelect from '@/common/components/inputs/MultiSelect.vue';
import ResearchFilterModal from '@/organisation/components/research/ResearchFilterModal.vue';
import { mapGetters } from 'vuex';
import { createResearchExport } from '@/organisation/api';
import { getProvinces } from '@/organisation/services/researches';

const FILTERABLE_RESEARCH_ITEM_TYPES = [
  'stars',
  'check_boxes',
  'radio_buttons',
];

export default {
  name: 'ResearchOptionsBar',
  components: {
    'v-button': Button,
    'v-dropdown-menu': DropdownMenu,
    'v-dropdown-menu-button': DropdownMenuButton,
    'v-multi-select': MultiSelect,
    'v-research-filter-modal': ResearchFilterModal,
  },
  data() {
    return {
      loader: { loading: false },
      navbarFixed: false,
      showAllFilters: false,
      baseDOMContainer: document.getElementById('organisation-container'),
    };
  },
  methods: {
    async exportFile(fileType) {
      this.loader.loading = true;
      const response = await createResearchExport(this.research.uuid, fileType);
      document.location.href = `/api/org/research-exports/${response.data.uuid}/download/`;
      this.loader.loading = false;
    },
    reset() {
      this.$store.commit('researches/resetFilters');
      this.$emit('filter');
    },
    setNavbarFixed() {
      this.navbarFixed = this.baseDOMContainer.scrollTop > 120;
    },
    submitFilters() {
      this.$emit('filter');
      this.showAllFilters = false;
    },
  },
  computed: {
    ...mapGetters(['user']),
    ...mapGetters('researches', [
      'research',
      'researchItems',
      'filters',
      'loadingResearchReviewSessions',
      'loadingResearchStatistics',
    ]),
    filterLoader() {
      return {
        loading: this.loadingResearchReviewSessions || this.loadingResearchStatistics,
      };
    },
    sideNavCollapsed() {
      return localStorage.getItem('collapseNav') === 'true';
    },
    filterableResearchItems() {
      return this.researchItems.filter(
        (researchItem) => (
          FILTERABLE_RESEARCH_ITEM_TYPES.includes(researchItem.item.render_type)
        ),
      );
    },
    regionOptions() {
      return this.research ? getProvinces(this.research.country) : [];
    },
  },
  created() {
    this.baseDOMContainer.addEventListener('scroll', this.setNavbarFixed);
  },
  destroyed() {
    this.baseDOMContainer.removeEventListener('scroll', this.setNavbarFixed);
  },
};
</script>

<style scoped>
input.input-age {
  width: 110px;
}

nav.fixed-top {
  top: 0;
  margin-left: 250px;
  z-index: 50;
}

nav.fixed-top.nav-wide {
  margin-left: 50px;
}

.nav-wrapper {
  min-height: 60px;
}

@media (max-width: 991px) {
  nav.fixed-top {
    padding-left: 1rem;
  }
}
</style>
